import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import billingApi from '@/lib/billingApi'
import { uiLogger } from '@/lib/debug'
import type { PackageSummary } from '@/lib/packageBillingApi'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import PackageCostGauge from './PackageCostGauge'
import { confirmExistingPackagePayment, type MinimumPaymentState } from '@/lib/packageMinimumPayment'

export type { PackageSummary } from '@/lib/packageBillingApi'

const stateLabels: Record<string, string> = {
  preparing: 'Preparing trial', trial: 'Free trial', paid: 'Paid service',
  payment_gated: 'Payment required', retained_recovery: 'World retained for recovery',
  payment_gated_retained: 'Payment required · world retained',
  cancelled_retained: 'Canceled · world retained temporarily',
  trial_expired_retained: 'Trial ended · world retained temporarily',
  grant_expired_retained: 'Transition grant ended · world retained temporarily',
  deleted: 'Deleted', legacy_grant: 'Legacy transition grant',
}
const apiFailure = (value: unknown): { response?: { status?: number; data?: { error?: string } } } => (
  typeof value === 'object' && value !== null ? value as { response?: { status?: number; data?: { error?: string } } } : {}
)

export function PackageMinimumAuthentication({ serverUuid, expectedAmount, paymentState, autoConfirm = false, onComplete }: {
  serverUuid: string; expectedAmount?: { minor: number; currency: string }
  paymentState?: MinimumPaymentState; autoConfirm?: boolean; onComplete?: () => void
}) {
  const [working, setWorking] = useState(false)
  const [message, setMessage] = useState('')
  const [state, setState] = useState<MinimumPaymentState>(paymentState || 'none')
  const [statusVersion, setStatusVersion] = useState(0)
  const confirming = useRef(false)
  const automaticallyStarted = useRef(false)
  const complete = useRef(onComplete)
  complete.current = onComplete

  useEffect(() => {
    if (paymentState) { setState(paymentState); return }
    const controller = new AbortController()
    const check = async () => {
      try {
        const response = await billingApi.get<{ data: { server_uuid: string; state: MinimumPaymentState } }>(
          '/hoard-api/billing/package-minimum-status', { params: { server_uuid: serverUuid }, signal: controller.signal })
        if (!controller.signal.aborted && response.data.data.server_uuid === serverUuid) setState(response.data.data.state)
      } catch {
        if (!controller.signal.aborted) setMessage('We couldn’t check your payment yet.')
      }
    }
    void check()
    const timer = window.setInterval(() => void check(), 10000)
    return () => { controller.abort(); window.clearInterval(timer) }
  }, [serverUuid, paymentState, statusVersion])

  const authenticate = async () => {
    if (confirming.current || state !== 'requires_action') return
    confirming.current = true
    setWorking(true)
    setMessage('')
    try {
      await confirmExistingPackagePayment(serverUuid, expectedAmount)
      setState('processing')
      setMessage('Payment confirmed. Getting your server ready…')
      complete.current?.()
    } catch (failure: unknown) {
      uiLogger.error('Package bank confirmation failed', { status: apiFailure(failure).response?.status })
      setMessage(failure instanceof Error && !apiFailure(failure).response ? failure.message : 'We couldn’t confirm the payment. Please try again.')
    } finally {
      confirming.current = false
      setWorking(false)
    }
  }

  const authenticateCurrent = useRef(authenticate)
  authenticateCurrent.current = authenticate
  useEffect(() => {
    if (autoConfirm && state === 'requires_action' && !automaticallyStarted.current) {
      automaticallyStarted.current = true
      void authenticateCurrent.current()
    }
  }, [autoConfirm, state])

  if (state === 'payment_method_required') return <p className="mt-4 text-sm">Your card wasn’t accepted. <Link to="/billing-settings" className="font-semibold underline">Update payment method</Link></p>
  if (state !== 'requires_action') return message ? <p role="status" className="mt-3 text-sm">{message}{state === 'none' && <button type="button" onClick={() => { setMessage(''); setStatusVersion(value => value + 1) }} className="ml-2 underline">Try again</button>}</p> : null

  return <div className="mt-3 rounded-lg border border-amber-500/20 p-3 text-sm">
    <p>Your bank needs a quick confirmation.</p>
    <button type="button" disabled={working} onClick={() => void authenticate()} className="mt-2 min-h-11 rounded-lg bg-violet-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
      {working ? 'Confirming with your bank…' : 'Confirm with bank'}
    </button>
    {message && <p role="status" className="mt-2">{message}</p>}
  </div>
}

export function PackageBillingSummaryView({ summary }: { summary: PackageSummary }) {
  const pkg = summary.package
  return (
    <section className="rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.07] to-white p-4 shadow-md dark:to-[#0d1117] sm:p-6" aria-labelledby="package-billing-heading">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-violet-300">This month</p>
      <div className="package-cost-metadata"><span>Package: {pkg.display_name}</span><span>{stateLabels[summary.state] || summary.state}</span></div>
      <PackageCostGauge summary={summary} headingId="package-billing-heading" />
      <PackageAccessNotice summary={summary} />
    </section>
  )
}

export function PackageBillingOverviewView({ summary }: { summary: PackageSummary }) {
  const pkg = summary.package
  return (
    <section className="console-v2-panel package-overview-panel" aria-labelledby="package-overview-heading">
      <p className="console-v2-eyebrow">This month</p>
      <div className="package-cost-metadata"><span>Package: {pkg.display_name}</span><span>{stateLabels[summary.state] || summary.state}</span></div>
      <PackageCostGauge summary={summary} headingId="package-overview-heading" />
      <PackageAccessNotice summary={summary} />
    </section>
  )
}

function PackageAccessNotice({ summary }: { summary: PackageSummary }) {
  const paymentRestricted = ['payment_gated', 'payment_gated_retained', 'trial_expired_retained', 'grant_expired_retained'].includes(summary.state)
  const deadlineLabel = summary.state === 'trial' ? 'Free until' : summary.state.includes('retained') ? 'Recovery ends' : 'Access until'
  return <>
    {paymentRestricted && <p className="mt-4 flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"><ExclamationTriangleIcon className="h-4 w-4 shrink-0" />Payment required to start this server.</p>}
    {summary.exact_deadline && <p className="mt-3 text-center text-xs text-gray-600 dark:text-gray-400">{deadlineLabel} <strong>{new Date(summary.exact_deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</strong></p>}
  </>
}

export default function PackageBillingSummaryCard({ serverUuid, variant = 'full' }: { serverUuid: string; variant?: 'full' | 'overview' }) {
  const [summary, setSummary] = useState<PackageSummary | null>(null)
  const [error, setError] = useState('')
  const [notPackage, setNotPackage] = useState(false)
  const [requestVersion, setRequestVersion] = useState(0)

  useEffect(() => {
    let active = true
    setSummary(null); setError(''); setNotPackage(false)
    billingApi.get<{ data: PackageSummary }>(`/subscription/servers/${serverUuid}/package-summary`)
      .then(response => { if (active) setSummary(response.data.data) })
      .catch((failure: unknown) => {
        if (!active) return
        const api = apiFailure(failure)
        if (api.response?.status === 404) setNotPackage(true)
        else {
          uiLogger.error('Package billing summary unavailable', failure)
          setError(api.response?.data?.error || 'Package billing details are unavailable.')
        }
      })
    return () => { active = false }
  }, [serverUuid, requestVersion])

  if (variant === 'overview' && (error || notPackage)) return <section className="console-v2-panel package-overview-panel package-overview-unavailable"><header><div><p className="console-v2-eyebrow">This month</p><h3>Usage temporarily unavailable</h3></div></header><p>We couldn’t load this server’s current usage.</p><button type="button" className="console-v2-text-button" onClick={() => setRequestVersion(value => value + 1)}><ArrowPathIcon />Retry</button></section>
  if (notPackage) return null
  if (error) return <section className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] p-4 text-sm text-amber-800 dark:text-amber-200"><p>We couldn’t load your current spend.</p><button type="button" className="mt-2 min-h-11 font-semibold underline" onClick={() => setRequestVersion(value => value + 1)}>Try again</button></section>
  if (!summary) return <section className={variant === 'overview' ? 'console-v2-panel package-overview-panel package-overview-loading' : 'rounded-xl border border-gray-200 p-4 text-sm text-gray-500 dark:border-white/[0.06]'}>Loading your current spend…</section>
  if (variant === 'overview') return <>
    <PackageBillingOverviewView summary={summary} />
    {summary.state === 'trial_expired_retained' && <PackageMinimumAuthentication serverUuid={serverUuid} />}
  </>
  return <>
    <PackageBillingSummaryView summary={summary} />
    {summary.state === 'trial_expired_retained' && <PackageMinimumAuthentication serverUuid={serverUuid} />}
  </>
}
